import { Rarity, Tag, type ModDef } from '../../sim/mods';
import { Stat } from '../../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Mods défensifs et utilitaires.

   Ils n'augmentent pas le DPS, et c'est leur raison d'être. Un pool composé
   uniquement de dégâts transforme la montée de niveau en formalité : on prend
   la plus grosse carte, il n'y a rien à arbitrer.

   Ces mods-là créent le vrai choix — survivre plus longtemps, ou frapper plus
   fort maintenant. C'est cet arbitrage qui fait qu'un joueur se souvient de sa
   run.
   ═══════════════════════════════════════════════════════════════════════════ */

const mods: ModDef[] = [
  {
    id: 'bouclier-orbital',
    name: 'Bouclier orbital',
    rarity: Rarity.Rare,
    tags: [Tag.Orbital],
    maxStacks: 2,
    modifiers: [{ stat: Stat.MaxLives, op: 'add', value: 1 }],
    text: '+1 vie',
  },
  {
    id: 'champ-de-graze',
    name: 'Champ de frôlement',
    rarity: Rarity.Common,
    tags: [Tag.Orbital],
    // Élargir le graze augmente le score ET la sécurité perçue : le joueur ose
    // s'approcher, ce qui est exactement le comportement que le genre récompense.
    modifiers: [
      { stat: Stat.GrazeRadius, op: 'mul', value: 0.3 },
      { stat: Stat.CellGain, op: 'mul', value: 0.18 },
    ],
    text: '+30 % de rayon de frôlement, +18 % de cellules',
  },
  {
    id: 'stabilisateur',
    name: 'Stabilisateur',
    rarity: Rarity.Common,
    tags: [Tag.Orbital],
    modifiers: [{ stat: Stat.MoveSpeed, op: 'mul', value: 0.12 }],
    text: '+12 % de vitesse de déplacement',
  },
  {
    id: 'reserve-bombes',
    name: 'Réserve de bombes',
    rarity: Rarity.Common,
    tags: [Tag.Orbital],
    maxStacks: 2,
    modifiers: [{ stat: Stat.MaxBombs, op: 'add', value: 1 }],
    text: '+1 bombe',
  },
  {
    id: 'collecteur',
    name: 'Collecteur',
    rarity: Rarity.Common,
    tags: [Tag.Drone],
    modifiers: [{ stat: Stat.CellGain, op: 'mul', value: 0.35 }],
    text: '+35 % de cellules',
  },
  {
    id: 'pilote-agile',
    name: 'Pilote agile',
    rarity: Rarity.Common,
    tags: [Tag.Surcharge],
    modifiers: [{ stat: Stat.MoveSpeed, op: 'mul', value: 0.16 }],
    text: '+16 % de vitesse de déplacement',
  },

  /* ── J12 — la survie a maintenant un prix ─────────────────────────────────
     Les six cartes ci-dessus sont des bonus purs : les prendre ne coûte que le
     tour de niveau. À 49 mods, ça ne suffit plus — un pool où la moitié des
     cartes sont sans contrepartie ramène le choix à « quelle est la plus
     grosse ». Celles qui suivent monnaient une ressource contre une autre, y
     compris entre deux systèmes de score (frôlement contre cellules), pour que
     même une carte sans dégâts pose une vraie question.
     ─────────────────────────────────────────────────────────────────────── */
  {
    id: 'nacelle-blindee',
    name: 'Nacelle blindée',
    rarity: Rarity.Rare,
    // Un seul exemplaire : une vie supplémentaire est la récompense la plus
    // forte du pool, et « Bouclier orbital » en offre déjà deux sans contrepartie.
    maxStacks: 1,
    tags: [Tag.Orbital],
    modifiers: [
      { stat: Stat.MaxLives, op: 'add', value: 1 },
      { stat: Stat.MaxBombs, op: 'add', value: 1 },
      { stat: Stat.MoveSpeed, op: 'mul', value: -0.12 },
    ],
    text: '+1 vie, +1 bombe, −12 % de vitesse de déplacement',
  },
  {
    id: 'aimant-cellulaire',
    name: 'Aimant cellulaire',
    rarity: Rarity.Common,
    tags: [Tag.Drone, Tag.Orbital],
    maxStacks: 2,
    // Deux sources de score échangées l'une contre l'autre : le ramassage
    // garanti contre le frôlement, qui rapporte plus mais exige de s'exposer.
    modifiers: [
      { stat: Stat.CellGain, op: 'mul', value: 0.45 },
      { stat: Stat.GrazeRadius, op: 'mul', value: -0.2 },
    ],
    text: '+45 % de cellules, −20 % de rayon de frôlement',
  },
  {
    id: 'propulseurs-vectoriels',
    name: 'Propulseurs vectoriels',
    rarity: Rarity.Rare,
    tags: [Tag.Orbital, Tag.Surcharge],
    maxStacks: 2,
    // Trois effets, pas quatre : la version précédente ajoutait du frôlement et
    // la carte devenait illisible d'un coup d'œil. Une carte qu'on doit relire
    // pendant une pause de montée de niveau a déjà échoué (§6.12).
    modifiers: [
      { stat: Stat.MoveSpeed, op: 'mul', value: 0.28 },
      { stat: Stat.FireRate, op: 'mul', value: 0.07 },
      { stat: Stat.CellGain, op: 'mul', value: -0.18 },
    ],
    text: '+28 % de vitesse de déplacement, +7 % de cadence, −18 % de cellules',
  },
  {
    id: 'torche-de-proximite',
    name: 'Torche de proximité',
    rarity: Rarity.Rare,
    tags: [Tag.Thermique, Tag.Drone],
    maxStacks: 2,
    // La carte du joueur qui accepte de rester bas dans l'écran : de la
    // puissance contre la capacité à se replier.
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.18 },
      { stat: Stat.FireRate, op: 'mul', value: 0.11 },
      { stat: Stat.MoveSpeed, op: 'mul', value: -0.14 },
    ],
    text: '+18 % de dégâts, +11 % de cadence, −14 % de vitesse de déplacement',
  },
];

export default mods;
